import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { ArrowRight, Menu } from "lucide-react";

import logo from "@/assets/logo.png";
import logoLight from "@/assets/logo-light.png";
import schwansoftWide from "@/assets/schwansoft-wide.png";
import heroWorkshop from "@/assets/hero-workshop.jpg";
import { LanguageToggle } from "@/components/LanguageToggle";

const LazyHomeSections = lazy(() => import("../home-sections"));

export const Route = createFileRoute("/")({
  component: Index,
});

const NAV_IDS = ["home", "about", "tyres", "services", "faqs", "contact"] as const;

function useActiveSection() {
  const [active, setActive] = useState("home");
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    NAV_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setActive(id);
          });
        },
        { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);
  return active;
}

function Logo({ className = "", variant = "dark" }: { className?: string; variant?: "dark" | "light" }) {
  return (
    <a href="#home" className={`flex items-center gap-3 ${className}`}>
      <img
        src={variant === "light" ? logoLight : logo}
        alt="Sony Tyres — Safety & Trust"
        width={220}
        height={64}
        className="h-10 w-auto sm:h-11"
      />
    </a>
  );
}

function Nav() {
  const active = useActiveSection();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
        <Logo />

        <div className="hidden items-center gap-7 md:flex">
          {NAV_IDS.map((id) => (
            <a
              key={id}
              href={`#${id}`}
              className={`text-sm font-medium transition-colors hover:text-accent ${
                active === id ? "text-accent" : "text-foreground/80"
              }`}
            >
              {t(`nav.${id}`)}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <LanguageToggle />
          <a
            href="#contact"
            className="hidden bg-foreground px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest text-background transition-colors hover:bg-accent hover:text-accent-foreground md:inline-flex"
          >
            {t("nav.visit")}
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              aria-label={t("nav.menu")}
              className="inline-flex h-10 w-10 items-center justify-center border border-border text-foreground md:hidden"
            >
              <Menu className="size-5" />
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] max-w-sm bg-background p-0">
              <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
              <div className="flex h-16 items-center border-b border-border px-6">
                <Logo />
              </div>
              <div className="flex flex-col gap-1 px-6 py-6">
                {NAV_IDS.map((id) => (
                  <a
                    key={id}
                    href={`#${id}`}
                    onClick={() => setOpen(false)}
                    className={`py-3 font-display text-2xl transition-colors hover:text-accent ${
                      active === id ? "text-accent" : "text-foreground"
                    }`}
                  >
                    {t(`nav.${id}`)}
                  </a>
                ))}
                <a
                  href="#contact"
                  onClick={() => setOpen(false)}
                  className="mt-6 inline-flex items-center justify-center bg-foreground px-4 py-4 text-xs font-bold uppercase tracking-widest text-background"
                >
                  {t("nav.visit")}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}

function Counter({
  value,
  suffix = "",
  label,
}: {
  value: number;
  suffix?: string;
  label: string;
}) {
  const [n, setN] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const duration = 1600;
          const start = performance.now();
          const tick = (now: number) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(value * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          obs.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);
  return (
    <div ref={ref} className="text-center">
      <div className="font-display text-4xl font-medium text-accent sm:text-5xl md:text-6xl">
        {n}
        {suffix}
      </div>
      <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <header id="home" className="relative overflow-hidden px-4 pb-16 pt-10 sm:px-6 sm:pt-12">
      <div className="mx-auto grid max-w-7xl gap-10 md:gap-12">
        <div className="max-w-3xl animate-reveal">
          <span className="mb-5 inline-block font-mono text-[11px] uppercase tracking-[0.3em] text-accent sm:text-[11px]">
            {t("hero.eyebrow")}
          </span>
          <h1 className="mb-6 text-balance font-display text-4xl font-medium leading-[1] sm:text-5xl md:mb-8 md:text-7xl lg:text-8xl">
            {t("hero.title_a")} <span className="italic text-accent">{t("hero.title_b")}</span> {t("hero.title_c")}
          </h1>
          <p className="mb-8 max-w-3xl font-display text-base italic leading-relaxed text-muted-foreground sm:text-lg md:mb-10 md:text-xl">
            {t("hero.sub")}
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <a
              href="#tyres"
              className="inline-flex items-center gap-2 bg-foreground px-5 py-3 text-xs font-bold uppercase tracking-widest text-background transition-colors hover:bg-accent hover:text-accent-foreground sm:px-6"
            >
              {t("hero.cta_explore")} <ArrowRight className="size-4" />
            </a>
            <a
              href="#about"
              className="inline-flex items-center gap-2 border border-foreground/20 px-5 py-3 text-xs font-bold uppercase tracking-widest text-foreground transition-colors hover:border-foreground sm:px-6"
            >
              {t("hero.cta_story")}
            </a>
          </div>
        </div>

        <div className="animate-reveal [animation-delay:200ms]">
          <img
            src={heroWorkshop}
            alt={t("hero.image_alt")}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1920}
            height={1080}
            className="aspect-[16/10] w-full rounded-sm object-cover shadow-xl ring-1 ring-foreground/5 sm:aspect-[21/9]"
          />
        </div>

        <div className="grid grid-cols-2 gap-6 border-y border-border py-10 sm:gap-8 md:grid-cols-4 md:py-12">
          <Counter value={40} suffix="+" label={t("stats.years")} />
          <Counter value={6} label={t("stats.brands")} />
          <Counter value={100} suffix="%" label={t("stats.guarantee")} />
          <Counter value={50000} suffix="+" label={t("stats.customers")} />
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="fixed inset-x-0 bottom-0 z-40 h-20 border-t border-border bg-foreground px-4 text-background sm:px-6">
      <div className="mx-auto grid h-full max-w-7xl items-center gap-4 text-center md:grid-cols-[auto_1fr_auto] md:gap-8 md:text-left">
        <div className="flex flex-col items-center justify-center gap-1 md:items-start">
          <img
            src={logoLight}
            alt="Sony Tyres — Safety & Trust"
            width={220}
            height={64}
            loading="lazy"
            className="h-10 w-[202px] object-contain sm:h-11 sm:w-[202px]"
          />
          <div className="font-mono text-[11px] uppercase tracking-widest text-background/60">
            {t("footer.tagline").split("·")[0]?.trim()}
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-x-7 gap-y-2 md:justify-center">
          {NAV_IDS.map((id) => (
            <a
              key={id}
              href={`#${id}`}
              className="text-sm font-medium text-background/80 transition-colors hover:text-accent"
            >
              {t(`nav.${id}`)}
            </a>
          ))}
        </div>

        <div className="flex flex-col items-center justify-center gap-1 md:justify-self-end md:items-start">
          <div className="flex w-[240px] items-center justify-between overflow-hidden">
            <div className="font-mono text-[11px] uppercase tracking-widest text-background/60">
              © 2026 Sony Tyres
            </div>
            <img
              src={schwansoftWide}
              alt="Schwansoft Solutions LLP"
              loading="lazy"
              className="h-10 w-auto origin-left scale-110"
              style={{ mixBlendMode: "screen", opacity: 0.95 }}
            />
          </div>
        </div>
      </div>
    </footer>
  );
}

function Index() {
  return (
    <div className="flex min-h-screen flex-col bg-background font-sans text-foreground">
      <Nav />
      <main className="flex-1 pb-20">
        <Hero />
        <Suspense
          fallback={
            <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
              <div className="h-96 rounded-sm border border-border bg-secondary/40" />
            </div>
          }
        >
          <LazyHomeSections />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
